import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Divider, Paper, Box, Typography, Chip, Fade } from '@mui/material';
import moment from 'moment';
import TableComponent from '../UI/TableComponent';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import CloseIcon from '@mui/icons-material/Close';

const AttendanceDetailPopup = ({ open, name, attendance, onClose }) => {
  if (!attendance || !Array.isArray(attendance)) {
    // If attendance is null or not an array, handle it accordingly
    return (
      <Dialog open={open} onClose={onClose} maxWidth="">
        {/* Your content for handling null or invalid attendance */}
        <DialogTitle>Error: Attendance data is unavailable</DialogTitle>
        <DialogContent>No attendance data available</DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Sort attendance in descending order based on the date and time to show the latest entry first
  const sortedAttendance = [...attendance].sort((a, b) => {
    const dateTimeA = moment(`${a.date} ${a.time.split(' - ')[0]}`, 'YYYY-MM-DD h:mm A');
    const dateTimeB = moment(`${b.date} ${b.time.split(' - ')[0]}`, 'YYYY-MM-DD h:mm A');
    return dateTimeB - dateTimeA;
  });



  const getDaysPresented = (month) => {
    const presentedDates = new Set();

    // Get all unique dates in the specified month where user was present
    attendance.forEach((entry) => {
      const entryMonth = moment(entry.date, 'YYYY-MM-DD').format('YYYY-MM');
      if (entryMonth === month && entry.status !== 'false') {
        presentedDates.add(entry.date);
      }
    });

    return presentedDates.size;
  };



  const getDaysOff = (month) => {
    const daysInMonth = moment(month, 'YYYY-MM').daysInMonth();
    const startOfMonth = moment(month, 'YYYY-MM').startOf('month');
    const today = moment();

    let daysOffCount = 0;

    for (let i = 0; i < daysInMonth; i++) {
      const currentDay = startOfMonth.clone().add(i, 'days');
      const currentDayString = currentDay.format('YYYY-MM-DD');
      const isInAttendance = attendance.some((entry) => entry.date === currentDayString);
      const isFutureDate = currentDay.isAfter(today, 'day');
      const isWeekend = currentDay.day() === 0 || currentDay.day() === 6; // Sunday = 0, Saturday = 6

      // Only count weekdays that are not in the future and have no attendance record
      if (!isInAttendance && !isFutureDate && !isWeekend) {
        daysOffCount++;
      }
    }

    return daysOffCount;
  };



  const getTotalHoursSpent = (month) => {
    // Filter attendance for the specific month
    const attendanceForMonth = attendance.filter((entry) => {
      const entryMonth = moment(entry.date, 'YYYY-MM-DD').format('YYYY-MM');
      return entryMonth === month;
    });

    // Group attendance by date to handle daily time ranges properly
    const dailyAttendance = {};
    attendanceForMonth.forEach((entry) => {
      if (!dailyAttendance[entry.date]) {
        dailyAttendance[entry.date] = [];
      }
      dailyAttendance[entry.date].push(entry);
    });

    let totalMonthHours = 0;

    // Calculate hours for each day and sum them up
    Object.keys(dailyAttendance).forEach((date) => {
      const dayEntries = dailyAttendance[date];
      const timeRanges = [];

      dayEntries.forEach((entry) => {
        try {
          let startTime, endTime;
          
          // Use individual startTime and endTime fields if available
          if (entry.startTime && entry.endTime) {
            startTime = moment(`${entry.date} ${entry.startTime}`, 'YYYY-MM-DD HH:mm');
            endTime = moment(`${entry.date} ${entry.endTime}`, 'YYYY-MM-DD HH:mm');
          } else if (entry.time && entry.time.includes(' - ')) {
            const timeRange = entry.time.split(' - ');
            if (timeRange.length === 2) {
              const startTimeStr = timeRange[0].trim();
              const endTimeStr = timeRange[1].trim();
              
              if (startTimeStr.match(/^\d{1,2}:\d{2}$/) && endTimeStr.match(/^\d{1,2}:\d{2}$/)) {
                startTime = moment(`${entry.date} ${startTimeStr}`, 'YYYY-MM-DD HH:mm');
                endTime = moment(`${entry.date} ${endTimeStr}`, 'YYYY-MM-DD HH:mm');
              } else {
                startTime = moment(`${entry.date} ${startTimeStr}`, 'YYYY-MM-DD h:mm A');
                endTime = moment(`${entry.date} ${endTimeStr}`, 'YYYY-MM-DD h:mm A');
              }
            }
          }

          if (startTime && endTime && startTime.isValid() && endTime.isValid()) {
            if (endTime.isBefore(startTime)) {
              endTime.add(1, 'day');
            }
            
            const hoursSpent = endTime.diff(startTime, 'hours', true);
            if (hoursSpent > 0 && hoursSpent <= 24) {
              timeRanges.push({
                start: startTime.clone(),
                end: endTime.clone()
              });
            }
          }
        } catch (error) {
          console.warn('Error parsing time for entry:', entry, error);
        }
      });

      // Merge overlapping time ranges for this day
      timeRanges.sort((a, b) => a.start - b.start);
      let mergedRanges = [];

      timeRanges.forEach((range) => {
        if (mergedRanges.length === 0) {
          mergedRanges.push(range);
        } else {
          const lastRange = mergedRanges[mergedRanges.length - 1];
          if (range.start.isSameOrBefore(lastRange.end)) {
            if (range.end.isAfter(lastRange.end)) {
              lastRange.end = range.end.clone();
            }
          } else {
            mergedRanges.push(range);
          }
        }
      });

      // Add this day's hours to the monthly total
      mergedRanges.forEach((range) => {
        totalMonthHours += range.end.diff(range.start, 'hours', true);
      });
    });

    return totalMonthHours.toFixed(2);
  };

  const getTodayHoursSpent = () => {
    const today = moment().format('YYYY-MM-DD');
    const todayAttendance = attendance.filter((entry) => entry.date === today);

    // Collect all valid time ranges for today
    const timeRanges = [];

    todayAttendance.forEach((entry) => {
      try {
        let startTime, endTime;
        
        // Use individual startTime and endTime fields if available
        if (entry.startTime && entry.endTime) {
          startTime = moment(`${entry.date} ${entry.startTime}`, 'YYYY-MM-DD HH:mm');
          endTime = moment(`${entry.date} ${entry.endTime}`, 'YYYY-MM-DD HH:mm');
        } else if (entry.time && entry.time.includes(' - ')) {
          const timeRange = entry.time.split(' - ');
          if (timeRange.length === 2) {
            const startTimeStr = timeRange[0].trim();
            const endTimeStr = timeRange[1].trim();
            
            if (startTimeStr.match(/^\d{1,2}:\d{2}$/) && endTimeStr.match(/^\d{1,2}:\d{2}$/)) {
              startTime = moment(`${entry.date} ${startTimeStr}`, 'YYYY-MM-DD HH:mm');
              endTime = moment(`${entry.date} ${endTimeStr}`, 'YYYY-MM-DD HH:mm');
            } else {
              startTime = moment(`${entry.date} ${startTimeStr}`, 'YYYY-MM-DD h:mm A');
              endTime = moment(`${entry.date} ${endTimeStr}`, 'YYYY-MM-DD h:mm A');
            }
          }
        }

        if (startTime && endTime && startTime.isValid() && endTime.isValid()) {
          if (endTime.isBefore(startTime)) {
            endTime.add(1, 'day');
          }
          
          const hoursSpent = endTime.diff(startTime, 'hours', true);
          if (hoursSpent > 0 && hoursSpent <= 24) {
            timeRanges.push({
              start: startTime.clone(),
              end: endTime.clone(),
              hours: hoursSpent
            });
          }
        }
      } catch (error) {
        console.warn('Error parsing time for today entry:', entry, error);
      }
    });

    // Sort time ranges by start time
    timeRanges.sort((a, b) => a.start - b.start);

    // Merge overlapping time ranges and calculate total hours
    let totalHours = 0;
    let mergedRanges = [];

    timeRanges.forEach((range) => {
      if (mergedRanges.length === 0) {
        mergedRanges.push(range);
      } else {
        const lastRange = mergedRanges[mergedRanges.length - 1];
        
        // Check if current range overlaps with the last merged range
        if (range.start.isSameOrBefore(lastRange.end)) {
          // Merge ranges - extend the end time if current range ends later
          if (range.end.isAfter(lastRange.end)) {
            lastRange.end = range.end.clone();
          }
        } else {
          // No overlap, add as new range
          mergedRanges.push(range);
        }
      }
    });

    // Calculate total hours from merged ranges
    mergedRanges.forEach((range) => {
      totalHours += range.end.diff(range.start, 'hours', true);
    });

    return totalHours.toFixed(2);
  };

  const columns = [
    { 
      field: 'S. No', 
      headerName: 'S. No', 
      width: 80, 
      renderCell: (params) => (
        <Chip 
          label={params.row.id} 
          size="small" 
          sx={{ 
            backgroundColor: '#f0f9ff', 
            color: '#0369a1',
            fontWeight: 600
          }} 
        />
      )
    },
    { 
      field: 'date', 
      headerName: 'Date', 
      width: 130,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarTodayIcon sx={{ color: '#6366f1', fontSize: 16 }} />
          <Typography variant="body2" sx={{ color: '#374151' }}>
            {params.value}
          </Typography>
        </Box>
      )
    },
    { 
      field: 'time', 
      headerName: 'Time', 
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccessTimeIcon sx={{ color: '#10b981', fontSize: 16 }} />
          <Typography variant="body2" sx={{ color: '#374151' }}>
            {params.value}
          </Typography>
        </Box>
      )
    },
    {
      field: 'agenda',
      headerName: 'Description',
      autoHeight: true,
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ py: 1 }}>
          <Typography variant="body2" sx={{ 
            whiteSpace: 'pre-line',
            color: '#374151',
            lineHeight: 1.5
          }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
  ];
  // Calculate values
  const currentMonth = moment();
  const currentMonthName = currentMonth.format('MMMM');
  const currentMonthFormatted = currentMonth.format('YYYY-MM');
  const daysPresent = getDaysPresented(currentMonthFormatted);
  const daysOff = getDaysOff(currentMonthFormatted);
  const totalHoursSpent = getTotalHoursSpent(currentMonthFormatted);
  const todayHours = getTodayHoursSpent();

  const getRowHeight = (params) => {
    return params.model.agenda.split('\n').length * 20 + 50; // Set your desired height
  };
  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth 
      TransitionComponent={Fade}
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          overflow: 'hidden'
        }
      }}
    >
      {/* Header */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        p: 2
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <PersonIcon sx={{ fontSize: 28 }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                {name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmailIcon sx={{ fontSize: 16 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {attendance && attendance[0] && attendance[0].email}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Button
            onClick={onClose}
            startIcon={<CloseIcon />}
            sx={{
              color: 'white',
              borderColor: 'rgba(255,255,255,0.3)',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)',
                borderColor: 'rgba(255,255,255,0.5)'
              }
            }}
            variant="outlined"
          >
            Close
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Box sx={{ p: 2, backgroundColor: '#f8fafc' }}>
        <Typography variant="h6" sx={{ 
          mb: 3, 
          fontWeight: 600, 
          color: '#1e293b',
          textAlign: 'center'
        }}>
          Monthly Summary - {currentMonthName}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Paper sx={{ p: 1.5, borderRadius: '8px', minWidth: 90, textAlign: 'center' }}>
            <EventAvailableIcon sx={{ color: '#10b981', fontSize: 20, mb: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#10b981', fontSize: '1rem' }}>
              {daysPresent}
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.75rem' }}>
              Days Present
            </Typography>
          </Paper>
          
          <Paper sx={{ p: 1.5, borderRadius: '8px', minWidth: 90, textAlign: 'center' }}>
            <EventBusyIcon sx={{ color: '#ef4444', fontSize: 20, mb: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#ef4444', fontSize: '1rem' }}>
              {daysOff}
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.75rem' }}>
              Days Off
            </Typography>
          </Paper>
          
          <Paper sx={{ p: 1.5, borderRadius: '8px', minWidth: 90, textAlign: 'center' }}>
            <AccessTimeIcon sx={{ color: '#3b82f6', fontSize: 20, mb: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#3b82f6', fontSize: '1rem' }}>
              {totalHoursSpent}h
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.75rem' }}>
              Month Hours
            </Typography>
          </Paper>
          
          <Paper sx={{ p: 1.5, borderRadius: '8px', minWidth: 90, textAlign: 'center' }}>
            <CalendarTodayIcon sx={{ color: '#8b5cf6', fontSize: 20, mb: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#8b5cf6', fontSize: '1rem' }}>
              {todayHours}h
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.75rem' }}>
              Today Hours
            </Typography>
          </Paper>
        </Box>
      </Box>

      <DialogContent sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1e293b' }}>
          Activity Log
        </Typography>
        <Box sx={{ 
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)'
        }}>
          <TableComponent rows={sortedAttendance} columns={columns} getRowHeight={getRowHeight} />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AttendanceDetailPopup;
